import { useEffect, useState } from 'react';
import { fetchAuditDetail } from '../services/auth';

/**
 * AuditDetail - Read-only detail view (spec audit-review-console R2).
 *
 * Per-policy cards (status, reasoning, confidence, evaluation time,
 * priority-review badge), the recommendation block and the metrics footer.
 * Heavy detail comes lazy-loaded from S3 through the console Lambda; when the
 * payload is unavailable the run summary still renders.
 */

function StatusBadge({ status }) {
  const cls =
    status === 'compliant' ? 'badge-completed' :
    status === 'non_compliant' ? 'badge-reject' :
    status === 'insufficient_data' ? 'badge-downgrade' :
    'badge-muted';
  return <span className={`badge ${cls}`}>{String(status).replace(/_/g, ' ')}</span>;
}

function VerdictBadge({ value }) {
  if (!value) return <span className="badge badge-muted">not recommended yet</span>;
  const cls =
    value === 'accept' ? 'badge-accept' :
    value === 'reject' ? 'badge-reject' :
    'badge-downgrade';
  return <span className={`badge ${cls}`}>{value}</span>;
}

function ConfidenceBar({ value }) {
  const pct = Math.round((value ?? 0) * 100);
  return (
    <div className="confidence">
      <div className="confidence-track">
        <div className="confidence-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="confidence-value mono">{value != null ? `${pct}%` : '—'}</span>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export function AuditDetail({ traceId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const detail = await fetchAuditDetail(traceId);
        if (!cancelled) setData(detail);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [traceId]);

  if (loading) {
    return (
      <div className="queue-state loading">
        <span className="spinner"></span> Loading audit {traceId}…
      </div>
    );
  }

  if (error) {
    return (
      <div className="detail">
        <button className="btn-link" onClick={onBack}>← Back to queue</button>
        <div className="queue-state error">Error: {error}</div>
      </div>
    );
  }

  const policies = data?.results ? Object.entries(data.results) : [];
  const hasPriority = policies.some(([, card]) => card.priority_review);

  return (
    <section className="detail">
      <button className="btn-link" onClick={onBack}>← Back to queue</button>

      <header className="detail-header">
        <div className="detail-title">
          <h2 className="mono">{data.trace_id}</h2>
          {data.pack && (
            <span className="pill pack-pill">
              {data.pack}{data.pack_version != null ? ` · v${data.pack_version}` : ''}
            </span>
          )}
        </div>
        <div className="detail-badges">
          {data.status === 'FAILED'
            ? <span className="badge badge-failed">FAILED</span>
            : <span className="badge badge-completed">{data.status}</span>}
          <span className="pill">{data.review_status || '—'}</span>
        </div>
      </header>

      <div className="detail-meta mono">
        <span>created {formatDate(data.created_at)}</span>
        {data.completed_at && <span> · completed {formatDate(data.completed_at)}</span>}
      </div>

      {hasPriority && (
        <div className="priority-banner">
          <span className="priority-dot" /> Priority review — at least one required policy
          returned insufficient data.
        </div>
      )}

      {data.status === 'FAILED' && (
        <div className="failure-panel">
          <h3>Run failure</h3>
          <p className="failure-error">{data.error || 'Unknown error'}</p>
          {Array.isArray(data.attempts) && data.attempts.length > 0 && (
            <details className="attempts">
              <summary>Provider attempts ({data.attempts.length})</summary>
              <ul>
                {data.attempts.map((attempt, i) => (
                  <li key={i} className="mono">
                    {attempt.model || attempt.provider || `attempt ${i + 1}`}
                    {attempt.error ? ` — ${attempt.error}` : ''}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {!data.detail_available && (
        <div className="degraded-panel">
          Full detail for this run is no longer available; showing the stored summary.
        </div>
      )}

      {data.recommendation && (
        <div className="recommendation-card">
          <div className="card-head">
            <h3>Recommendation</h3>
            <VerdictBadge value={data.recommendation.recommendation} />
          </div>
          {data.recommendation.downgrade_to && (
            <p className="downgrade-line">
              Downgrade to <span className="mono">{data.recommendation.downgrade_to}</span>
            </p>
          )}
          <ConfidenceBar value={data.recommendation.confidence} />
          {data.recommendation.reasoning && (
            <p className="reasoning">{data.recommendation.reasoning}</p>
          )}
        </div>
      )}

      {policies.length > 0 && (
        <div className="policy-section">
          <h3>Policies ({policies.length})</h3>
          <div className="policy-grid">
            {policies.map(([policyId, card]) => (
              <article key={policyId} className={`policy-card ${card.status || ''}`}>
                <div className="card-head">
                  <h4 className="mono">{policyId}</h4>
                  {card.priority_review && (
                    <span className="badge badge-priority">
                      <span className="priority-dot" /> priority
                    </span>
                  )}
                </div>
                <StatusBadge status={card.status} />
                <ConfidenceBar value={card.confidence} />
                <p className="reasoning">{card.reasoning || 'No reasoning recorded.'}</p>
                <footer className="policy-foot mono">
                  {card.evaluation_time_ms != null && <span>{card.evaluation_time_ms} ms</span>}
                </footer>
              </article>
            ))}
          </div>
        </div>
      )}

      {data.input_data && (
        <details className="input-summary">
          <summary>Input summary</summary>
          <pre className="mono">{JSON.stringify(data.input_data, null, 2)}</pre>
        </details>
      )}
    </section>
  );
}
